import { useCallback } from 'react';
import { useToast } from 'hooks/useToast';

import { useAppSelector, useAppDispatch } from 'store/hooks/useStore';

import * as customerApi from 'api/customerApi';
import * as menuApi from 'api/menuApi';
import {
  contractListActions,
  contractListSelectors,
} from 'store/reducers/customerReducer/contractListSlice';
import { listSelectors } from 'store/reducers/customerReducer/listSlice';
import { Contract } from 'types/contract';

/**
 * 契約一覧 store の実装をコンポーネントから隠すための中間層
 * @returns selector と action のラッパー
 */
export const useContractListStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const loading = useAppSelector(contractListSelectors.loading);
  const finalUpdateTime = useAppSelector(contractListSelectors.finalUpdateTime);
  const contracts = useAppSelector(contractListSelectors.contracts);
  const uncontractedMenus = useAppSelector(
    contractListSelectors.uncontractedMenus,
  );
  const uncontractedMenusAddedCurrentMenu = useAppSelector(
    contractListSelectors.uncontractedMenusAddedCurrentMenu,
  );

  const menuMap = useAppSelector(contractListSelectors.menuMap);
  const selectedContract = useAppSelector(
    contractListSelectors.selectedContract,
  );
  // 契約一覧取得用
  const selectedCustomer = useAppSelector(listSelectors.selectedCustomer);

  const init = useCallback(() => {
    dispatch(contractListActions.init());
  }, [dispatch]);

  const fetchContracts = useCallback(async () => {
    if (!selectedCustomer) {
      return;
    }

    dispatch(contractListActions.loadStarted());
    const contractsResult = await customerApi.fetchContracts(
      selectedCustomer.id,
    );
    const menusResult = await menuApi.fetchMenus();
    if (!contractsResult.ok) {
      toast({
        status: 'error',
        description: `契約一覧の取得に失敗しました: ${contractsResult.err.message}`,
      });
    } else if (!menusResult.ok) {
      toast({
        status: 'error',
        description: `メニュー一覧の取得に失敗しました: ${menusResult.err.message}`,
      });
    } else {
      const originalContracts = contractsResult.value;
      const originalMenus = menusResult.value;

      dispatch(
        contractListActions.fetchContractsSuccess({
          originalContracts,
          originalMenus,
        }),
      );
    }
    dispatch(contractListActions.loadEnded());
  }, [dispatch, toast, selectedCustomer]);

  const selectContract = useCallback(
    (contract: Contract) => {
      dispatch(contractListActions.contractSelected({ contract }));
    },
    [dispatch],
  );

  const clearSelectedContract = useCallback(() => {
    dispatch(contractListActions.selectedContractCleared());
  }, [dispatch]);

  return {
    loading,
    contracts,
    uncontractedMenus,
    uncontractedMenusAddedCurrentMenu,
    menuMap,
    selectedContract,
    finalUpdateTime,
    init,
    fetchContracts,
    selectContract,
    clearSelectedContract,
  };
};
